import { forwardRef, useId, useMemo, useState } from "react";
import clsx from "clsx";
import { ChevronDownIcon, PlusIcon } from "@heroicons/react/24/outline";
import { useTagLibrarySearch } from "@/shared/hooks/useTagLibrarySearch";
import { menuCategories } from "@/widget-components/newsquawk-headlines/models/categories";
import { tagKindLabel } from "../../builderModel";
import ChipInput, { type ChipSuggestion } from "./ChipInput";
import { CHIP_TONE } from "./chipTone";
import type { KeywordTag } from "./useKeywordTags";

export interface PickedTag {
  id: number;
  name: string;
  category?: string;
}

interface TagSearchFieldProps {
  query: string;
  onQueryChange: (next: string) => void;
  /** Ids already in the box, left out of the results. */
  selectedIds: Set<string>;
  onAdd: (tag: PickedTag) => void;
  hasChips: boolean;
}

/**
 * The Tags box's typing field, and what opens beneath it.
 *
 * Search covers the whole tag library — companies, topics, places — each
 * result named with its kind. Ticker tags are left out: headlines carry the
 * company's tag, and a ticker tag's label is not a symbol anyone types.
 * Browse opens the seven curated groups. A keyword that is also a tag gets a
 * one-click suggestion.
 */
export const TagSearchField = forwardRef<HTMLInputElement, TagSearchFieldProps>(
  function TagSearchField(
    { query, onQueryChange, selectedIds, onAdd, hasChips }: TagSearchFieldProps,
    ref
  ) {
    const { tags, isLoading, isEmpty } = useTagLibrarySearch(query);
    const results = useMemo(
      () =>
        tags
          .filter((t) => t.category !== "ticker" && !selectedIds.has(String(t.tag_id)))
          .slice(0, 8),
      [tags, selectedIds]
    );
    const suggestions: ChipSuggestion[] = results.map((t) => ({
      key: String(t.tag_id),
      label: t.name,
      detail: tagKindLabel(t.category),
    }));

    return (
      <ChipInput
        ref={ref}
        value={query}
        onChange={onQueryChange}
        suggestions={suggestions}
        pickFirstOnEnter
        commitsText={false}
        onPick={(s) => {
          const tag = results.find((t) => String(t.tag_id) === s.key);
          if (!tag) return;
          onAdd({ id: tag.tag_id, name: tag.name, category: tag.category });
          onQueryChange("");
        }}
        emptyText={
          query.trim().length < 2
            ? undefined
            : isLoading
              ? "Searching…"
              : isEmpty || results.length === 0
                ? "No tags match"
                : undefined
        }
        placeholder={
          hasChips ? "Search companies and topics" : "Search companies and topics, e.g. Apple, OPEC"
        }
        ariaLabel="Search Newsquawk tags"
      />
    );
  }
);

/** "Suggested from your keywords: + OPEC". */
export function KeywordTagSuggestions({
  tags,
  selectedIds,
  onAdd,
}: {
  tags: KeywordTag[];
  selectedIds: Set<string>;
  onAdd: (tag: PickedTag) => void;
}) {
  const offered = tags.filter((t) => !selectedIds.has(String(t.id)));
  if (offered.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-xs text-gray-400">Suggested from your keywords</span>
      {offered.map((tag) => (
        <button
          key={tag.id}
          type="button"
          onClick={() => onAdd({ id: tag.id, name: tag.name, category: tag.category })}
          aria-label={`Add the ${tag.name} tag`}
          className="inline-flex h-6 items-center gap-1 rounded border border-dashed border-green-400/45 bg-transparent px-2 py-0 text-xs font-normal text-green-200 transition-colors hover:bg-green-900/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-squawk-blue/70"
        >
          <PlusIcon className="size-3" strokeWidth={2} aria-hidden="true" />
          {tag.name}
        </button>
      ))}
    </div>
  );
}

/** The seven curated tag groups, opened from "Browse". */
export function TagBrowse({
  selectedIds,
  onToggle,
}: {
  selectedIds: Set<string>;
  onToggle: (tag: PickedTag) => void;
}) {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const baseId = useId();
  return (
    <div className="flex flex-col gap-0.5 border-t border-gray-800 pb-0.5 pt-1.5">
      {menuCategories.map((group, index) => {
        const isOpen = !!open[group.title];
        const ticked = group.items.filter((t) => selectedIds.has(String(t.id))).length;
        const panelId = `${baseId}-${index}`;
        return (
          <div key={group.title} className="flex flex-col">
            <button
              type="button"
              onClick={() => setOpen((prev) => ({ ...prev, [group.title]: !prev[group.title] }))}
              aria-expanded={isOpen}
              aria-controls={panelId}
              className="flex h-[30px] items-center gap-1.5 rounded border-0 bg-transparent px-0.5 py-0 text-left text-[13px] font-normal text-gray-300 transition-colors hover:text-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-squawk-blue/70"
            >
              <span className="flex-1">{group.title}</span>
              {ticked > 0 && <span className="text-xs text-squawk-blue">{ticked}</span>}
              <ChevronDownIcon
                aria-hidden="true"
                strokeWidth={2}
                className={clsx(
                  "size-3 text-gray-400 transition-transform duration-150 motion-reduce:transition-none",
                  isOpen && "rotate-180"
                )}
              />
            </button>
            {isOpen && (
              <div id={panelId} className="flex flex-wrap gap-1.5 pb-2.5 pt-0.5">
                {group.items.map((tag) => {
                  const on = selectedIds.has(String(tag.id));
                  return (
                    <button
                      key={tag.id}
                      type="button"
                      aria-pressed={on}
                      onClick={() => onToggle({ id: tag.id, name: tag.title, category: "category" })}
                      className={clsx(
                        "h-[26px] rounded border px-2.5 py-0 text-xs font-normal transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-squawk-blue/70",
                        on
                          ? CHIP_TONE.tag
                          : "border-gray-700 bg-transparent text-gray-300 hover:border-gray-500 hover:text-gray-100"
                      )}
                    >
                      {tag.title}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
